import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { TwoFactorAuthService } from '../../../services/two-factor/two-factor-auth.service';
import { TwoFactorSetupData } from '../../../models/two-factor.model';
import Swal from 'sweetalert2';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-setup-2fa-modal',
  templateUrl: './setup-2fa-modal.component.html',
  styleUrls: ['./setup-2fa-modal.component.css']
})
export class Setup2FaModalComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() isVisible: boolean = false;
  @Input() uid: string = '';
  @Input() email: string = '';
  
  @Output() setupComplete = new EventEmitter<string[]>(); // Emits backup codes
  @Output() cancelled = new EventEmitter<void>();

  isLoading: boolean = false;
  isInitializing: boolean = false;
  setupData: TwoFactorSetupData | null = null;
  safeQrCodeUrl: SafeUrl | null = null;
  showBackupCodes: boolean = false;
  backupCodes: string[] = [];
  
  // 6 distinct input values
  codeValues: string[] = ['', '', '', '', '', ''];

  constructor(
    private twoFactorService: TwoFactorAuthService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isVisible'] && this.isVisible) {
      this.resetState();
      this.initializeSetup();
    } else if (changes['isVisible'] && !this.isVisible) {
      this.hideModal();
    }
  }

  ngAfterViewInit() {
    if (this.isVisible) {
      this.initializeSetup();
    }
  }
  
  private resetState() {
    this.codeValues = ['', '', '', '', '', ''];
    this.setupData = null;
    this.safeQrCodeUrl = null;
    this.showBackupCodes = false;
    this.backupCodes = [];
    this.isInitializing = false;
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  private async initializeSetup() {
    if (!this.uid || !this.email) {
      return;
    }

    this.isInitializing = true;
    this.showModal(); // Show immediately so they see the loader

    try {
      this.setupData = await this.twoFactorService.generateSecret(this.uid, this.email);
      if (this.setupData?.qrCodeUrl) {
        this.safeQrCodeUrl = this.sanitizer.bypassSecurityTrustUrl(this.setupData.qrCodeUrl);
      }
    } catch (error) {
      Swal.fire('Error', 'No se pudo inicializar la configuración 2FA', 'error');
      this.onCancel();
    } finally {
      this.isInitializing = false;
      this.cdr.detectChanges();
      
      // Focus the first input after rendering
      setTimeout(() => {
        const inputs = document.querySelectorAll('.code-input-box');
        if (inputs && inputs[0]) (inputs[0] as HTMLInputElement).focus();
      }, 100);
    }
  }

  private showModal() {
    setTimeout(() => {
      const modalElement = document.getElementById('setup2FAModal');
      if (modalElement) {
        // Usar Bootstrap 5 para mostrar el modal
        const modal = new (window as any).bootstrap.Modal(modalElement, {
          backdrop: 'static',
          keyboard: false
        });
        modal.show();
      }
    }, 50);
  }

  private hideModal() {
    const modalElement = document.getElementById('setup2FAModal');
    if (modalElement) {
      const modalInstance = (window as any).bootstrap?.Modal?.getInstance(modalElement);
      if (modalInstance) {
        modalInstance.hide();
      }
    }
  }

  // Input Handling
  onInput(event: any, index: number) {
    const value = event.target.value.replace(/\D/g, ''); // Extract only numbers
    this.codeValues[index] = value;
    event.target.value = value;
    
    // Jump to next box if filled
    if (value && index < 5) {
      const inputs = document.querySelectorAll('.code-input-box');
      (inputs[index + 1] as HTMLInputElement)?.focus();
    }
  }

  onKeydown(event: KeyboardEvent, index: number) {
    // Jump to previous box on backspace if current is empty
    if (event.key === 'Backspace' && !this.codeValues[index] && index > 0) {
      const inputs = document.querySelectorAll('.code-input-box');
      (inputs[index - 1] as HTMLInputElement)?.focus();
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasteData = event.clipboardData?.getData('text')?.replace(/\D/g, '') || '';
    if (pasteData) {
      const inputs = document.querySelectorAll('.code-input-box');
      for (let i = 0; i < 6 && i < pasteData.length; i++) {
        this.codeValues[i] = pasteData[i];
        if (inputs[i]) (inputs[i] as HTMLInputElement).value = pasteData[i];
      }
      
      const focusIndex = Math.min(pasteData.length, 5);
      if (inputs[focusIndex]) (inputs[focusIndex] as HTMLInputElement).focus();
    }
  }
  
  get fullCode(): string {
    return this.codeValues.join('');
  }
  
  get isCodeComplete(): boolean {
    return this.fullCode.length === 6 && !this.isLoading;
  }

  async onSubmit() {
    if (!this.isCodeComplete || !this.setupData?.secret) return;

    this.isLoading = true;

    try {
      this.backupCodes = await this.twoFactorService.confirmSetupAPI(this.setupData.secret, this.fullCode);
      this.showBackupCodes = true;
      localStorage.setItem(`2fa_status_${this.uid}`, 'true');
      this.cdr.detectChanges();
    } catch (error: any) {
      const status = error?.status;
      const message = error?.error?.message || error?.message || '';
      const isInvalidCode = status === 400 && /código/i.test(message);
      Swal.fire({
        title: isInvalidCode ? 'Código incorrecto' : 'Error',
        text: isInvalidCode ? 'Código incorrecto. Por favor, verifica tu app.' : 'Ocurrió un error al verificar el código',
        icon: 'error',
        confirmButtonColor: '#ff8c00'
      });
      this.codeValues = ['', '', '', '', '', ''];
      this.cdr.detectChanges();
      const inputs = document.querySelectorAll('.code-input-box');
      inputs.forEach(input => (input as HTMLInputElement).value = '');
      if (inputs[0]) (inputs[0] as HTMLInputElement).focus();
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  completedSetup() {
    this.setupComplete.emit(this.backupCodes);
    this.isVisible = false;
    this.hideModal();
  }

  copyBackupCodes() {
    if (this.backupCodes.length === 0) return;
    const codeText = this.backupCodes.join('\n');
    navigator.clipboard.writeText(codeText)
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Copiado',
          text: 'Códigos copiados al portapapeles',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
      });
  }

  onCancel() {
    this.cancelled.emit();
    this.isVisible = false;
    this.hideModal();
  }
}
